<template>
  <Tabs
    ref="tabListContainer"
    :value="value"
    :scrollable="false"
    :class="$style.sbTabs"
    data-sb-regression="sb-tabs-list-dropdown-overflow"
  >
    <TabList>
      <template
        v-for="(tab) in visibleTabs"
        :key="tab.id"
      >
        <template v-if="$slots['tab-template']">
          <slot
            name="tab-template"
            :tab="tab"
          />
        </template>
        <SbTabNext
          v-else
          :disabled="tab.disabled"
          :delimit="tab.delimit"
          :tab="tab.id"
        >
          <span>
            <sb-text
              :data-sb-automation="tab.name"
              :value="tab.name"
            />
          </span>
        </SbTabNext>
      </template>
      <div
        v-if="visibleTabs.length < tabs.length"
        ref="overflowTabContainer"
      >
        <SbTabNext
          data-sb-automation="overflow-tab"
          tab="overflow"
          :class="activeInDropdown ? getActiveTabClasses(): ''"
          :delimit="true"
        >
          <span>
            <sb-text :value="overflowButtonText" />
          </span>
          <span
            :class="[$style.overflowTabIcon, 'pi', 'pi-angle-down', isOverflowTabDropdownVisible ? $style.open : '']"
          />
          <SbPopover
            :active="isOverflowTabDropdownVisible"
            :boundaries-element="boundariesElement()"
            placement="end"
            orientation="bottom"
            data-sb-automation="overflow-tab-dropdown"
          >
            <FadeTransition>
              <div
                v-show="isOverflowTabDropdownVisible"
                :class="$style.tabsListOverflowDropdown"
              >
                <template
                  v-for="tab in overflowTabs"
                  :key="tab.id"
                >
                  <template v-if="$slots['tab-more-template']">
                    <slot
                      name="tab-more-template"
                      :class="activeTab === tab.id ? getActiveDropdownTabClasses(): ''"
                      :tab="tab"
                    />
                  </template>
                  <SbTabNext
                    v-else
                    :class="activeTab === tab.id ? getActiveDropdownTabClasses() : ''"
                    :tab="tab.id"
                    :pt="{
                      root: {
                        class: $style.overflowDropdownTab
                      },
                    }"
                  >
                    <SbEllipsis version="next">
                      <sb-text
                        :value="tab.name"
                      />
                    </SbEllipsis>
                  </SbTabNext>
                </template>
              </div>
            </FadeTransition>
          </SbPopover>
        </SbTabNext>
      </div>
    </TabList>
  </Tabs>
</template>

<script setup>
/* eslint-disable css-modules/no-unused-class */
import { provide, defineProps, watch, onMounted, ref, defineOptions, computed, nextTick, getCurrentInstance, onBeforeUnmount, useCssModule } from 'vue';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import SbText from '@src/components/Text/SbText.vue';
import SbPopover from '@src/components/Popover/SbPopover.vue';
import FadeTransition from '@src/components/Transitions/Fade.vue';
import { useClickOutside } from '@src/utils/composables/useClickOutside';
import debounce from 'lodash/debounce';
import ResizeObserver from 'resize-observer-polyfill';
import SbTabNext from '@src/components/Tabs/components/SbTabNext.vue';
import { getTabWidths, getNumberOfVisibleTabs } from '@src/components/Tabs/utils/tabListNextDropdownOverflow';
import SbEllipsis from '@src/components/Ellipsis/SbEllipsis.vue';

const $style = useCssModule();
defineOptions({
  name: 'SbTabsListNextDropdownOverflow',
});

const props = defineProps({
  /**
   * List of tabs objects to be displayed.
   * Each tab requires an `id` and `name` attribute
   */
  tabs: {
    type: Array,
    default: () => [],
  },
  /**
   * Used to bind the active SbTab. Allows you to use the `v-model` syntax.
   */
  value: {
    default: null,
    type: [String, Number],
  },
  /**
   * Text of the overflow button.
   */
  overflowButtonText: {
    type: String,
    default: 'common.more',
  },
  /**
   * The active tab to be displayed.
   */
  activeTab: {
    default: null,
    type: [String, Number],
  },
  /**
   * Function when active tab changes.
   */
  onActiveTab: {
    default: () => {},
    type: Function,
  },
});

let resizeObserver = null;
const tabListContainer = ref(null);
const tabElementDimensions = ref({
  widths: [],
  totalElementWidth: 0,
  moreButtonWidth: 0,
});
const countOfVisibleTabs = ref(props.tabs.length);
const visibleTabs = ref(props.tabs);
const isOverflowTabDropdownVisible = ref(false);
const overflowTabContainer = ref(null);

const activeInDropdown = computed(() => {
  const index = props.tabs.findIndex((t) => t.id === props.activeTab);
  return (index >= countOfVisibleTabs.value);
});
const overflowTabs = computed(() => {
  return props.tabs.slice(countOfVisibleTabs.value);
});

function recalculateTabMeasurements() {
  const $tabListEl = tabListContainer?.value?.$el;

  if (!$tabListEl) {
    return;
  }

  tabElementDimensions.value = getTabWidths($tabListEl?.querySelector('.p-tablist-tab-list'));

  countOfVisibleTabs.value = getNumberOfVisibleTabs({
    tabContainer: $tabListEl.querySelector('.p-tablist'),
    widths: tabElementDimensions.value.widths,
    totalElementWidth: tabElementDimensions.value.totalElementWidth,
    moreButtonWidth: tabElementDimensions.value.moreButtonWidth,
  });

  visibleTabs.value = props.tabs.slice(0, countOfVisibleTabs.value);
}

function hideOverflowDropdown() {
  isOverflowTabDropdownVisible.value = false;
}

function toggleOverflowDropdown() {
  isOverflowTabDropdownVisible.value = !isOverflowTabDropdownVisible.value;
}

function boundariesElement() {
  const instance = getCurrentInstance();
  return instance?.proxy?.$root.$el?.parentElement;
}

function getActiveTabClasses() {
  return 'p-tab p-tab-active sb-active-tab';
}

function getActiveDropdownTabClasses() {
  return $style.tabSelectedInDropdown;
}

async function setActiveTab(tab) {
  if (tab === 'overflow') {
    toggleOverflowDropdown();
    return;
  }
  hideOverflowDropdown();
  props.onActiveTab(tab);
}

function handleClickOutside() {
  hideOverflowDropdown();
}

useClickOutside(overflowTabContainer, handleClickOutside);

provide('setActiveTab', setActiveTab);

watch(
  () => props.tabs,
  async (newTabs) => {
    visibleTabs.value = newTabs;
    await nextTick();
    recalculateTabMeasurements();
  },
  { deep: true, immediate: true },
);

onMounted(async () => {
  if (!tabListContainer.value) {
    return;
  }

  // eslint-disable-next-line compat/compat
  resizeObserver = new ResizeObserver(
    debounce(async (entries) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const entry of entries) {
        if (entry.target === tabListContainer.value?.$el) {
          visibleTabs.value = props.tabs;
          // eslint-disable-next-line no-await-in-loop
          await nextTick();
          recalculateTabMeasurements();
        }
      }
    }, 200),
  );

  resizeObserver.observe(tabListContainer.value?.$el);
  await nextTick();
  recalculateTabMeasurements();
});

onBeforeUnmount(() => {
  resizeObserver?.unobserve(tabListContainer.value?.$el);
  resizeObserver?.disconnect();
});
</script>
<style lang="scss" module>
@use '@src/components/Tabs/style/sb-tabs.module.scss';
</style>
